/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import Notify from "@eisinc/App/Components/Notify";
import AvailableCreditsTable from "@eisinc/App/Components/PaymentCenter/AvailableCreditsTable";
import InvoicePaymentDetails from "@eisinc/App/Components/PaymentCenter/InvoicePaymentDetails";
import InvoicesRange from "@eisinc/App/Components/PaymentCenter/InvoicesRange";
import PaymentCenterStepper from "@eisinc/App/Components/PaymentCenter/PaymentCenterStepper";
import PaymentCenterSummary from "@eisinc/App/Components/PaymentCenter/PaymentCenterSummary";
import PaymentOnAccount, { PaymentOnAccountModel } from "@eisinc/App/Components/PaymentCenter/PaymentOnAccount";
import SelectInvoicesTable from "@eisinc/App/Components/PaymentCenter/SelectInvoicesTable";
import XngFraudSignOut from "@eisinc/App/Handlers/Context/XngFraudSignOut";
import getSystemList from "@eisinc/App/Handlers/XNG/getSystemList";
import { placeInvoicePayment } from "@eisinc/App/Services/InvoicePaymentService";
import { GetSystemListValuesModel } from "@eisinc/App/Services/SystemListService";
import { InvoicePaymentModel } from "@eisinc/XDL/interfaces";
import { color, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    invoicePaymentItems?: InvoicePaymentModel[];
    setIsOpenItemsStep: (value: boolean) => void;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        customSettings: getSettingsCollection(state).customSettings,
    };
};

const mapDispatchToProps = {
    getSystemList,
    XngFraudSignOut,
};

export interface SelectInvoicesStyle {
    mainGridContainer?: GridContainerProps;
    leftSection?: GridItemProps;
    rightSection?: GridItemProps;
    noResultsContainer?: InjectableCss;
}

export const styles: SelectInvoicesStyle = {
    mainGridContainer: {
        css: css`
            ${GridOffset} {
                align-items: flex-start;
            }
        `,
    },
    leftSection: {
        width: [12, 12, 12, 9, 9],
        css: css`
            flex-direction: column;
        `,
    },
    rightSection: {
        width: [12, 12, 12, 3, 3],
        css: css`
            flex-direction: column;
            position: sticky;
            top: 75px;
            ${xScreen("lte", "md")} {
                position: static;
            }
        `,
    },
    noResultsContainer: {
        css: css`
            margin-top: 20px;
            width: 100%;
        `,
    },
};

const Separator = styled.div`
    border-bottom: 1px solid ${color("common.ui4")};
    width: 100%;
    margin: 20px 0;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SelectInvoices: React.FunctionComponent<Props> = ({
    invoicePaymentItems,
    setIsOpenItemsStep,
    getSystemList,
    XngFraudSignOut,
    customSettings,
}) => {
    const steps = ["", "Select Invoices", "Available Credit", "Payment"];
    const [currentStep, setCurrentStep] = React.useState(1);
    const [selectedInvoicePayments, setSelectedInvoicePayments] = React.useState<InvoicePaymentModel[] | undefined>();
    const [selectedCredits, setSelectedCredits] = React.useState<InvoicePaymentModel[] | undefined>();
    const [paymentOnAccountData, setPaymentOnAccountData] = React.useState({} as PaymentOnAccountModel);
    const [dueDateRanges, setDueDateRanges] = React.useState<GetSystemListValuesModel[]>([]);

    const toasterContext = React.useContext(ToasterContext);

    const showPaymentOnAccountInInvoicePayment =
        (customSettings as any)?.showPaymentOnAccountInInvoicePayment?.toLowerCase() === "true";

    const renderNotFoundMessage = () => {
        if (!invoicePaymentItems?.length) {
            return (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Notify
                        messageType="danger"
                        notifyContainerOverrides={css`
                            max-width: 100%;
                        `}
                        body={siteMessage("Custom_Payment_Center_No_Invoices_Found")}
                    />
                </StyledWrapper>
            );
        }
    };

    const prepareInvoicePaymentsForSubmit = () => {
        const invoices = (selectedInvoicePayments || [])?.concat(selectedCredits || [])?.map(invoice => {
            return {
                invoiceNumber: invoice.invoiceItemNumber,
                amount: invoice.amountToPay ? parseFloat(invoice.amountToPay as any) : invoice.invoiceAmount,
                reasonCode: invoice.reasonForReducedPrice || null,
                notes: invoice.additionalComments || null,
                isCredit: invoice.invoiceAmount < 0,
            };
        });

        return invoices;
    };

    const onSubmit = (payload: any) => {
        payload.invoicePayments = prepareInvoicePaymentsForSubmit();
        if (paymentOnAccountData && Object.keys(paymentOnAccountData)?.length > 0) {
            payload.paymentOnAccount = paymentOnAccountData;
        }

        placeInvoicePayment(payload).then(
            (response: any) => {
                toasterContext.addToast({
                    body: siteMessage("Custom_Payment_Center_SUCCESS_PAYMENT_MESSAGE"),
                    messageType: "success",
                });
                setIsOpenItemsStep(true);
            },
            (error: any) => {
                toasterContext.addToast({
                    body: error?.message,
                    messageType: "danger",
                });

                if (error?.message === siteMessage("CreditCardInfo_MaximumCreditCardAttempts")) {
                    setTimeout(() => {
                        XngFraudSignOut();
                    }, 3000);
                } else {
                    setCurrentStep(1);
                }
            },
        );
    };

    const loadDueDateRanges = () => {
        getSystemList({
            systemListName: "Custom_InvoicePaymentDueDateRange",
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    const sortedData = resultProps.apiResult.result.sort(
                        (a, b) => parseInt(a.additionalInfo, 10) - parseInt(b.additionalInfo, 10),
                    );
                    setDueDateRanges(sortedData);
                }
            },
        });
    };

    React.useEffect(() => {
        loadDueDateRanges();
    }, []);

    return (
        <GridContainer gap={35} className="seelct-invoices-wrapper" {...styles.mainGridContainer}>
            <GridItem {...styles.leftSection}>
                <PaymentCenterStepper
                    steps={steps}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    setIsOpenItemsStep={setIsOpenItemsStep}
                />
                <Separator />
                {currentStep === 1 && (
                    <InvoicesRange
                        setDueDateRanges={setDueDateRanges}
                        dueDateRanges={dueDateRanges}
                        invoicePaymentItems={invoicePaymentItems}
                    />
                )}
                {renderNotFoundMessage()}
                {currentStep === 1 && (
                    <SelectInvoicesTable
                        invoicePaymentItems={invoicePaymentItems}
                        setSelectedInvoicePayments={setSelectedInvoicePayments}
                        dueDateRanges={dueDateRanges}
                    />
                )}
                <AvailableCreditsTable
                    invoicePaymentItems={invoicePaymentItems}
                    setSelectedCredits={setSelectedCredits}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    selectedInvoicePayments={selectedInvoicePayments}
                    paymentOnAccountData={paymentOnAccountData}
                />
                {currentStep === 3 && <InvoicePaymentDetails setCurrentStep={setCurrentStep} onSubmit={onSubmit} />}
            </GridItem>
            <GridItem {...styles.rightSection}>
                {currentStep === 1 && showPaymentOnAccountInInvoicePayment && (
                    <PaymentOnAccount
                        paymentOnAccountValue={paymentOnAccountData}
                        onAddToSummary={setPaymentOnAccountData}
                    />
                )}
                <PaymentCenterSummary
                    selectedInvoicePayments={selectedInvoicePayments}
                    selectedCredits={selectedCredits}
                    paymentOnAccountData={paymentOnAccountData}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                />
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectInvoices);
